
interface WebViewOptions {
  onBack: () => void;
}

/**
 * Check if client-side (browser)
 * @returns true if running in browser
 */
const isClientSide = (): boolean => {
  return typeof window !== 'undefined';
};

/**
 * Get WebView interface instance
 * @returns WebView interface or undefined
 */
const webViewInterface = (): any => {
  if (typeof window !== 'undefined') {
    return (window as any).WebViewInterface;
  }
  return undefined;
};

/**
 * Check if the current context is a WebView
 * @param ctx - Optional context object (can be req, or full context with pathname, query, etc.)
 * @returns true if running in WebView
 */
export const isWebView = (ctx?: any): boolean => {
  const pathname =
    ctx?.pathname || // for Next.js getInitialProps
    ctx?.req?._parsedUrl?.pathname || // for express request
    (isClientSide() ? window.location.pathname : null);

  // Check multiple WebView indicators
  return !!(
    // Client-side: Check if WebViewInterface exists
    (isClientSide() && (window as any)?.WebViewInterface) ||
    // Query parameter: client=app (Next.js getInitialProps)
    ctx?.query?.client === 'app' ||
    // Query parameter: client=app (express request)
    ctx?.req?.query?.client === 'app' ||
    // Client-side: Check URL search params
    (isClientSide() && new URLSearchParams(window.location.search).get('client') === 'app') ||
    // Request header: x-app-auth present
    ctx?.req?.headers?.['x-app-auth'] ||
    // Specific app payment result paths
    pathname === '/cart/payment/result/app' ||
    pathname === '/account/credit/payment/result/app'
  );
};

/**
 * Get authentication token from WebView or request headers
 * @param req - Optional request object with headers
 * @returns Token string or undefined
 */
export const getWebviewAccessToken = (req?: any): string | undefined => {
  const webview = webViewInterface();

  if (typeof webview?.fetchToken === 'function') {
    const token = webview.fetchToken();
    if (token) {
      return token;
    }
  }

  if (req?.headers?.['x-app-auth']) {
    return req.headers['x-app-auth'];
  }

  return undefined;
};

/**
 * Initialize Android WebView configuration
 * @param options - Router and history objects for navigation
 */
export const initializeWebView = (
  options: WebViewOptions
): (() => void) => {
  // Disable pull-to-refresh in WebView
  setRefreshEnabled(false);
  // The Agent is a focused WebView screen. Basalam owns the native bottom bar,
  // but it remains hidden while the Agent is open and is restored by the
  // native backFromWeb/back transition when the user leaves it.
  setBottomNavigationVisibility(false);
  // Setup Android back button handler
  const restoreAndroidBackHandler = setupAndroidBackHandler(options);

  setupUnUsedFunctions();
  return () => {
    restoreAndroidBackHandler();
  };
};

/**
 * Setup the Android back button navigation handler
 * @param options - Router and history objects
 */
const setupAndroidBackHandler = ({ onBack }: WebViewOptions): (() => void) => {
  if (typeof window === 'undefined') return () => undefined;

  const previousHandler = window.backFromAndroid;
  window.backFromAndroid = onBack;
  return () => {
    if (previousHandler) {
      window.backFromAndroid = previousHandler;
    } else {
      delete window.backFromAndroid;
    }
  };
};

const setRefreshEnabled = (state: boolean): void => {
  const webview = webViewInterface();
  if (typeof webview?.setRefreshEnabled === 'function') {
    webview.setRefreshEnabled(state);
  }
};

const setupUnUsedFunctions = ():void =>{

  if (typeof window !== 'undefined') {
    (window as any).getCurrentActiveTab = () => {};
    (window as any).onWebViewActivated = () => {};
  }
}

/**
 * Notify Android WebView that the page has mounted
 * Call this after authentication or when the app is ready
 */
export const notifyWebViewPageMounted = (): void => {
    const webview = webViewInterface();

    if (typeof webview?.onMountPage === 'function') {
      webview.onMountPage();
    }
};


/**
 * Set bottom navigation visibility in Android app
 * @param visible - true to show, false to hide bottom navigation
 */
export const setBottomNavigationVisibility = (visible: boolean): void => {
  if (typeof window !== 'undefined') {
    const webview = webViewInterface();

    if (typeof webview?.setBottomNavigationVisibility === 'function') {
      webview.setBottomNavigationVisibility(visible);
    }
  }
};

/**
 * Get session ID from webview bridge or fallback to cookie
 * @returns Session ID string or empty string
 */
export const getSessionId = (): string => {
  // In webview: use bridge method getSession()
  if (typeof window !== 'undefined' && isWebView()) {
    const webview = webViewInterface();
    try {
      if(webview){
      const sessionId = webview.getSession();
      if (sessionId) {
        return sessionId;
      }
      }

    } catch (error) {
      console.error('Error getting session from bridge:', error);
    }
  }

  // In browser: fallback to cookie
  if (typeof document !== 'undefined') {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; khabarchin-session-key=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || '';
    }
  }

  return '';
};


/**
 * Open external URL - uses WebView bridge if available, otherwise opens in new tab
 * This prevents adding to WebView history
 * @param url - URL to open
 */
export const openExternalUrl = (url: string): void => {
  if (typeof window === 'undefined') return;
  
  const webview = webViewInterface();
  
  // Try WebView bridge methods (common Android bridge patterns)
  if (webview) {
    // Try different common method names
    if (typeof webview.openUrl === 'function') {
      webview.openUrl(url);
      return;
    }
    if (typeof webview.openExternalUrl === 'function') {
      webview.openExternalUrl(url);
      return;
    }
    if (typeof webview.openInBrowser === 'function') {
      webview.openInBrowser(url);
      return;
    }
    if (typeof webview.openLink === 'function') {
      webview.openLink(url);
      return;
    }
  }
  
  // Fallback: open in new tab (works in browser, may add to history in WebView)
  window.open(url, '_blank', 'noopener,noreferrer');
};

/**
 * Leave the Agent through the native WebView history contract.
 *
 * `backFromWeb` consumes the current WebView page and restores the native
 * Basalam page (including its bottom navigation). Older app versions expose
 * `back` instead. Only a normal browser, or a bridge without either method,
 * uses the referrer fallback.
 */
export const returnToBasalam = (): void => {
  if (typeof window === 'undefined') return;

  // Charsou opens the Agent with window.location.href in the same WebView.
  // When a trusted Basalam referrer is present, browser history is therefore
  // the source of truth: going back consumes the Agent entry and restores the
  // exact Charsou page that opened it. Calling a native back method here can be
  // a no-op because no separate native-managed WebView screen was pushed.
  if (isWebView() && isTrustedBasalamUrl(document.referrer)) {
    window.history.back();
    return;
  }

  const webview = webViewInterface();
  if (typeof webview?.backFromWeb === 'function') {
    webview.backFromWeb();
    return;
  }
  if (typeof webview?.back === 'function') {
    webview.back();
    return;
  }

  window.location.replace(resolveBasalamReturnUrl(document.referrer));
};

export const isTrustedBasalamUrl = (value: string): boolean => {
  if (!value) return false;
  try {
    const url = new URL(value);
    const trustedHosts = ['basalam.com', 'basalam.dev'];
    return url.protocol === 'https:' && trustedHosts.some((host) =>
      url.hostname === host || url.hostname.endsWith(`.${host}`)
    );
  } catch {
    return false;
  }
};

export const resolveBasalamReturnUrl = (referrer: string): string => {
  const fallback = 'https://basalam.com';
  return isTrustedBasalamUrl(referrer) ? new URL(referrer).toString() : fallback;
};

export const hasInternalAgentHistory = (historyState: unknown): boolean => {
  if (!historyState || typeof historyState !== 'object') return false;
  const index = Number((historyState as { idx?: unknown }).idx ?? 0);
  return Number.isFinite(index) && index > 0;
};

// Extend Window interface for TypeScript
declare global {
  interface Window {
    WebViewInterface?: {
      onMountPage?: () => void;
      setRefreshEnabled?: (enabled: boolean) => void;
      setBottomNavigationVisibility?: (visible: boolean) => void;
      fetchToken?: () => string | undefined;
      version?: string;
      getSession?: () => string;
      backFromWeb?: () => void;
      back?: () => void;
      // External URL opening methods
      openUrl?: (url: string) => void;
      openExternalUrl?: (url: string) => void;
      openInBrowser?: (url: string) => void;
      openLink?: (url: string) => void;
    };
    // Bridge functions exposed directly on window

    getCurrentActiveTab?: () => void;
    onWebViewActivated?: (...args: unknown[]) => void;
    backFromAndroid?: () => void;

  }
}
